import { useState, useEffect, useCallback } from 'react';
import { admin } from '../../api/client.js';
import { LoadingSpinner } from '../../components/LoadingSpinner.js';
import { Badge } from '../../components/Badge.js';
import type { AdminUser, AdminTenant } from '../../types/index.js';

export function AdminWhitelistPage() {
  const [discordId, setDiscordId] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string } | null>(null);

  // whitelisted users table data
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [tableLoading, setTableLoading] = useState(true);

  const loadData = useCallback(async () => {
    setTableLoading(true);
    try {
      // load users and tenants independently so a failure in one doesn't block the other
      const usersRes = await admin.users();
      setUsers(usersRes.users);
    } catch (err) {
      console.error('[admin] failed to load users:', err);
    }
    try {
      const tenantsRes = await admin.tenants();
      setTenants(tenantsRes.tenants);
    } catch (err) {
      console.error('[admin] failed to load tenants:', err);
    }
    setTableLoading(false);
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const ownerIds = new Set(tenants.map((t) => t.ownerDiscordId));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!discordId) return;
    setSaving(true);
    setResult(null);
    try {
      const res = await admin.whitelist({ discordId });
      console.log('[admin] whitelisted user:', discordId, 'userId:', res.userId);
      setResult({ ok: true, message: `User whitelisted successfully (ID: ${res.userId})` });
      setDiscordId('');
      void loadData();
    } catch (err) {
      console.error('[admin] whitelist failed:', err);
      setResult({ ok: false, message: err instanceof Error ? err.message : 'Whitelist failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async (userId: string) => {
    try {
      await admin.revokeAccess({ userId });
      console.log('[admin] revoked access for:', userId);
      void loadData();
    } catch (err) {
      console.error('[admin] revoke failed:', err);
    }
  };

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'var(--fd)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 4 }}>Whitelist</h2>
        <p style={{ fontSize: 13, color: 'var(--t3)' }}>Grant Discord users access to the platform</p>
      </div>

      <div className="kpi" style={{ padding: 24, maxWidth: 520, marginBottom: 24 }}>
        <form onSubmit={(e) => void handleSubmit(e)} className="modal-form">
          <div className="form-group">
            <label className="form-label">Discord User ID</label>
            <input
              className="form-input"
              value={discordId}
              onChange={(e) => setDiscordId(e.target.value)}
              placeholder="e.g. 123456789012345678"
              style={{ fontFamily: 'monospace' }}
            />
          </div>

          {result && (
            <div className={`form-${result.ok ? 'success' : 'error'}`}>
              {result.message}
            </div>
          )}

          <button
            type="submit"
            className="btn-sm btn-primary"
            style={{ width: '100%', padding: 12, fontSize: 14 }}
            disabled={saving || !discordId}
          >
            {saving ? 'Whitelisting...' : 'Whitelist User'}
          </button>
        </form>
      </div>

      {/* Whitelisted users table */}
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ fontFamily: 'var(--fd)', fontSize: 15, fontWeight: 600, marginBottom: 12 }}>Whitelisted Users</h3>
      </div>

      {tableLoading ? (
        <LoadingSpinner text="Loading users..." />
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Discord Username</th>
                <th>Discord ID</th>
                <th>Status</th>
                <th>Has Server</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users
                .filter((u) => u.role !== 'platform_admin' && u.whitelisted)
                .map((u) => (
                  <tr key={u.id}>
                    <td style={{ color: 'var(--t1)', fontWeight: 500 }}>{u.discordUsername}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--t3)' }}>{u.discordId}</td>
                    <td>
                      <Badge
                        status={u.whitelisted ? 'active' : 'expired'}
                        label={u.whitelisted ? 'Approved' : 'Pending'}
                      />
                    </td>
                    <td>
                      <Badge
                        status={ownerIds.has(u.discordId) ? 'active' : 'expired'}
                        label={ownerIds.has(u.discordId) ? 'Yes' : 'No'}
                      />
                    </td>
                    <td>
                      {u.whitelisted && (
                        <button
                          className="btn-sm btn-ghost"
                          style={{ fontSize: 12, padding: '4px 10px' }}
                          onClick={() => void handleRevoke(u.id)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
